import { errorMessage } from '../../../../error-message.js';
import { getSessionFileChangeDirectoryFlags } from '../../session-file-change-path-kind.js';
import { readGitHeadBlob, readUtf8WorkspaceFile } from '../../workspace-files.js';
import { editSnippetToUnifiedDiff } from '../../../../files/diff/unified-diff.js';
import { PATH_SNAPSHOT_DEBOUNCE_MS } from './constants.js';
import { getToolCallId, isCompletedToolStatus } from './tool-key.js';

export type SessionFileChangePayload = {
  type: 'session_file_change';
  sessionId: string;
  runId: string;
  path: string;
  movedFromPath?: string;
  oldText: string;
  newText: string;
  patchContent: string;
  isDirectory: boolean;
  directoryRemoved: boolean;
};

/**
 * Debounced path snapshots + per-tool “before” maps for inferring session_file_change from disk.
 */
export class PathSnapshotTracker {
  private lastSeenRunId: string | undefined;
  private anonToolSeq = 0;
  private lastAnonymousToolKey: string | null = null;
  private readonly beforeByToolKey = new Map<string, Map<string, string>>();
  private readonly debouncers = new Map<string, ReturnType<typeof setTimeout>>();
  readonly accumulatedPathsByToolKey = new Map<string, Set<string>>();

  clearRunState(): void {
    for (const t of this.debouncers.values()) clearTimeout(t);
    this.debouncers.clear();
    this.beforeByToolKey.clear();
    this.accumulatedPathsByToolKey.clear();
    this.lastAnonymousToolKey = null;
  }

  onRunIdChanged(runId: string | undefined): void {
    if (runId && runId !== this.lastSeenRunId) {
      this.lastSeenRunId = runId;
      this.clearRunState();
    }
  }

  resolveToolKey(params: unknown, updateKind: string): string {
    const id = getToolCallId(params);
    if (id) return id;
    if (updateKind === 'tool_call') {
      this.lastAnonymousToolKey = `anon:${++this.anonToolSeq}`;
      return this.lastAnonymousToolKey;
    }
    return this.lastAnonymousToolKey ?? '_unknown';
  }

  resetToolSnapshots(toolKey: string): void {
    const t = this.debouncers.get(toolKey);
    if (t) clearTimeout(t);
    this.debouncers.delete(toolKey);
    this.beforeByToolKey.delete(toolKey);
    this.accumulatedPathsByToolKey.delete(toolKey);
  }

  captureBeforeFromDisk(toolKey: string, paths: string[], sessionParentPath: string): void {
    if (paths.length === 0) return;
    let m = this.beforeByToolKey.get(toolKey);
    if (!m) {
      m = new Map();
      this.beforeByToolKey.set(toolKey, m);
    }
    for (const p of paths) {
      if (m.has(p)) continue;
      m.set(p, readUtf8WorkspaceFile(sessionParentPath, p));
    }
  }

  ensureBeforeFromHeadForMissing(toolKey: string, paths: string[], sessionParentPath: string): void {
    let m = this.beforeByToolKey.get(toolKey);
    if (!m) {
      m = new Map();
      this.beforeByToolKey.set(toolKey, m);
    }
    for (const p of paths) {
      if (m.has(p)) continue;
      m.set(p, readGitHeadBlob(sessionParentPath, p));
    }
  }

  flushPathSnapshots(
    toolKey: string,
    sessionParentPath: string,
    sentPaths: Set<string>,
    send: ((p: SessionFileChangePayload) => void) | null,
    runId: string,
    sessionId: string,
    log: (msg: string) => void
  ): void {
    const t = this.debouncers.get(toolKey);
    if (t) clearTimeout(t);
    this.debouncers.delete(toolKey);

    const beforeMap = this.beforeByToolKey.get(toolKey);
    if (!beforeMap || beforeMap.size === 0) return;
    this.beforeByToolKey.delete(toolKey);

    if (!send || !runId || !sessionId) return;

    for (const [displayPath, oldText] of beforeMap) {
      const newText = readUtf8WorkspaceFile(sessionParentPath, displayPath);
      if (oldText === newText) continue;
      const patchContent = editSnippetToUnifiedDiff(displayPath, oldText, newText);
      const dirFlags = getSessionFileChangeDirectoryFlags(sessionParentPath, displayPath);
      try {
        send({
          type: 'session_file_change',
          sessionId,
          runId,
          path: displayPath,
          oldText,
          newText,
          patchContent,
          isDirectory: dirFlags.isDirectory,
          directoryRemoved: dirFlags.directoryRemoved,
        });
        sentPaths.add(displayPath);
      } catch (err) {
        log(`[Bridge service] Session file change failed for ${displayPath}: ${errorMessage(err)}`);
      }
    }
  }

  scheduleDebouncedFlush(
    toolKey: string,
    sessionParentPath: string,
    sentPaths: Set<string>,
    send: ((p: SessionFileChangePayload) => void) | null,
    runId: string,
    sessionId: string,
    log: (msg: string) => void
  ): void {
    const prev = this.debouncers.get(toolKey);
    if (prev) clearTimeout(prev);
    this.debouncers.set(
      toolKey,
      setTimeout(() => {
        this.debouncers.delete(toolKey);
        this.flushPathSnapshots(toolKey, sessionParentPath, sentPaths, send, runId, sessionId, log);
      }, PATH_SNAPSHOT_DEBOUNCE_MS)
    );
  }

  handleToolCallLifecycle(
    updateKind: string,
    toolKey: string,
    toolPaths: string[],
    status: unknown,
    sessionParentPath: string,
    sentPaths: Set<string>,
    send: ((p: SessionFileChangePayload) => void) | null,
    runId: string,
    sessionId: string,
    log: (msg: string) => void
  ): void {
    if (updateKind === 'tool_call') {
      this.resetToolSnapshots(toolKey);
    }

    if (updateKind === 'tool_call') {
      this.captureBeforeFromDisk(toolKey, toolPaths, sessionParentPath);
    } else if (updateKind === 'tool_call_update') {
      if (isCompletedToolStatus(status)) {
        this.ensureBeforeFromHeadForMissing(toolKey, toolPaths, sessionParentPath);
        this.flushPathSnapshots(toolKey, sessionParentPath, sentPaths, send, runId, sessionId, log);
      } else {
        this.captureBeforeFromDisk(toolKey, toolPaths, sessionParentPath);
        if (this.beforeByToolKey.has(toolKey)) {
          this.scheduleDebouncedFlush(toolKey, sessionParentPath, sentPaths, send, runId, sessionId, log);
        }
      }
    }
  }
}
